import { ceil, isEmpty } from "lodash";
import { Base, BaseIdOnly } from "../base";
import { Currency, DEFAULT_CURRENCY } from "../currency";
import { DEFAULT_LANGUAGE, Language } from "../language";
import { Network } from "../network";
import { PAYMENT_AMOUNT_PRECISION } from "./constants";
import { Discount, DiscountParam } from "./discount";
import { PaymentConfig, PaymentMethod } from "./payment";

export const DEFAULT_TRANSACTION_CONTACT_FIELD_KEYS = [
  "name",
  "email",
  "wallet",
];

export enum TransactionStatus {
  INITIAL = "initial",
  SUBMITTED = "submitted",
  PENDING = "pending",
  PROCESSING = "processing",
  SUCCEEDED = "succeeded",
  REFUNDED = "refunded",
  FAILED = "failed",
}

export interface TransactionContact {
  email?: string;
  name?: string;
  wallet?: string;
  phone?: string;
}

export interface TransactionItemParam extends Partial<TransactionItem> {
  productId: string; // usually tierId
}
export class TransactionItem extends BaseIdOnly {
  productId: string; // usually tierId
  name: string; // usually tier name
  price: number;
  quantity: number;
  contact: TransactionContact;
  priceId: string;
  extraInfo?: { key: string; value: string | string[] }[];
  createdAt: number;
  valid?: boolean;

  constructor(params: TransactionItemParam) {
    super(params);
    this.productId = params.productId;
    this.priceId = params.priceId || "";
    this.name = params.name || "";
    this.price = Number(params.price || 0);
    this.quantity = Number(params.quantity || 1) || 1;
    this.contact = params.contact || {};
    this.createdAt = Number(params.createdAt || new Date().valueOf());
    if (params.extraInfo && !isEmpty(params.extraInfo))
      this.extraInfo = params.extraInfo;
    if (params.valid) this.valid = params.valid;
  }
}

export interface TransactionAirdropResult {
  itemId: string;
  recipient: string;
  network: Network;
  contractAddress: string;
  txnHash: string;
  txnFee: number;
  userId?: string;
  email?: string;
}

export interface TransactionParam
  extends Omit<Partial<Transaction>, "items" | "discount"> {
  userId: string;
  eventId: string;
  items?: TransactionItemParam[];
  discount?: DiscountParam;
}

export class Transaction extends Base implements TransactionParam {
  userId: string;
  eventId: string;
  tierId: string;
  organizerId: string;
  contact: TransactionContact;

  eventName: string;
  language: Language;
  currency: Currency;
  status: TransactionStatus;
  items: TransactionItem[];
  fee: number; // 0.05 = 5%
  amount: number;
  finalAmount: number;
  webhookEvents: any[];
  refIds: { key: string; value: string }[];
  airdropResults: TransactionAirdropResult[];
  runnerResults?: any[];

  paymentMethod?: PaymentMethod;
  discount?: Discount;
  error?: string;
  referrer?: string; // referrer email / wallet
  referrerId?: string;
  referralCode?: string;

  metadata: {
    [key: string]: string;
  };

  constructor(params: TransactionParam) {
    super(params);
    this.userId = params.userId;
    this.eventId = params.eventId;
    this.organizerId = params.organizerId || "";
    this.contact = params.contact || {};
    this.eventName = params.eventName || "";

    this.status = params.status || TransactionStatus.INITIAL;
    this.language = params.language || DEFAULT_LANGUAGE;
    this.currency = params.currency || DEFAULT_CURRENCY;
    this.items = (params.items || [])
      .map((item) => new TransactionItem(item))
      .sort((a, b) => a.createdAt - b.createdAt);
    this.tierId = this.items.length > 0 ? this.items[0].productId || "" : "";
    this.webhookEvents = params.webhookEvents || [];
    this.refIds = params.refIds || [];
    this.airdropResults = params.airdropResults || [];
    this.fee = Number(params.fee ?? 0);
    this.amount = this.originalAmount;
    this.finalAmount = ceil(
      this.amount + this.transactionFee,
      PAYMENT_AMOUNT_PRECISION
    );
    this.metadata = params.metadata || {};

    if (params.runnerResults) this.runnerResults = params.runnerResults;
    if (params.paymentMethod) this.paymentMethod = params.paymentMethod;
    if (params.error) this.error = params.error;
    if (params.referrer) this.referrer = params.referrer;
    if (params.referrerId) this.referrerId = params.referrerId;
    if (params.referralCode) this.referralCode = params.referralCode;
    if (params.discount) {
      this.discount = new Discount(params.discount);
    }
    this.calculateFinalAmount();
  }

  get originalAmount() {
    return ceil(
      this.items.reduce((acc, cur) => (acc += cur.price * cur.quantity), 0),
      PAYMENT_AMOUNT_PRECISION
    );
  }

  get transactionFee() {
    if (!this.fee) return 0;
    return ceil(this.amount * this.fee, PAYMENT_AMOUNT_PRECISION);
  }

  get discountAmount() {
    try {
      if (!this.discount) return 0;
      const items =
        this.discount.applyLimit == -1
          ? this.items
          : this.items
              .filter((item) =>
                this.discount && this.discount.productId
                  ? item.productId == this.discount.productId
                  : true
              )
              .slice(0, this.discount.applyLimit);

      return Math.min(
        items.reduce(
          (acc, cur) =>
            (acc +=
              this.discount &&
              (this.discount.productId
                ? cur.productId == this.discount.productId
                : true)
                ? this.discount.calculateDiscount(cur.price * cur.quantity)
                : 0),
          0
        ),
        this.originalAmount
      );
    } catch (error) {}
    return 0;
  }

  // apply discount to each of the items
  applyDiscount(discount: Discount) {
    if (
      !discount?.code ||
      discount.value == undefined ||
      discount.value == null
    )
      return this;
    this.discount = discount;
    this.amount = ceil(
      Math.max(this.originalAmount - this.discountAmount, 0),
      PAYMENT_AMOUNT_PRECISION
    );
    this.finalAmount = ceil(
      this.amount + this.transactionFee,
      PAYMENT_AMOUNT_PRECISION
    );
    if (this.discount.isReferral) {
      if (this.discount.referrer) this.referrer = this.discount.referrer;
      if (this.discount.referrerId) this.referrerId = this.discount.referrerId;
      if (this.discount.code) this.referralCode = this.discount.code;
    }
    return this;
  }

  removeDiscount() {
    if (this.discount) {
      if (this.discount.isReferral) {
        if (this.discount.referrer === this.referrer) delete this.referrer;
        if (this.discount.referrerId === this.referrerId)
          delete this.referrerId;
        if (this.discount.code === this.referralCode) delete this.referralCode;
      }
      delete this.discount;
    }
    return this.calculateFinalAmount();
  }

  calculateFinalAmount() {
    this.amount = this.originalAmount;
    this.finalAmount = ceil(
      this.amount + this.transactionFee,
      PAYMENT_AMOUNT_PRECISION
    );
    if (this.discount) this.applyDiscount(this.discount);
    return this;
  }

  update() {
    this.updatedAt = new Date().valueOf();
    this.calculateFinalAmount();
    return this;
  }

  getAutoDiscount(paymentConfig: PaymentConfig) {
    if (!paymentConfig.autoDiscounts || isEmpty(paymentConfig.autoDiscounts))
      return null;
    for (const discountConfig of paymentConfig.autoDiscounts.sort(
      (a, b) => a.priority - b.priority
    )) {
      if (isEmpty(discountConfig.discount)) continue;
      // Quantity
      if (discountConfig.criteria.quantity) {
        if (this.items.length >= discountConfig.criteria.quantity) {
          return discountConfig.discount;
        }
      }
    }
    return null;
  }
}
